import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseYoutubePlayerOptions {
  onEnded?: () => void;
  enabled?: boolean;
}

declare global {
  interface Window {
    onYouTubeIframeAPIReady?: () => void;
    YT: typeof YT;
  }
}

export const useYoutubePlayer = (options: UseYoutubePlayerOptions = {}) => {
  const { onEnded, enabled = true } = options;
  const mountRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YT.Player | null>(null);
  const [apiReady, setApiReady] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(80);
  const [playbackRate, setPlaybackRateState] = useState<number>(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [availableRates, setAvailableRates] = useState<number[]>([0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]);
  const [pendingVideoId, setPendingVideoId] = useState<string | null>(null);

  // Load IFrame API once
  useEffect(() => {
    if (!enabled) return;
    if (window.YT && window.YT.Player) {
      setApiReady(true);
      return;
    }
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.body.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => setApiReady(true);
  }, [enabled]);

  // Create the player instance when API and mount are ready
  useEffect(() => {
    if (!enabled || !apiReady || !mountRef.current || playerRef.current) return;

    playerRef.current = new window.YT.Player(mountRef.current, {
      height: '1',
      width: '1',
      playerVars: {
        autoplay: 0,
        controls: 0,
        disablekb: 1,
        fs: 0,
        origin: window.location.origin,
        rel: 0,
        iv_load_policy: 3,
        playsinline: 1,
        modestbranding: 1,
      },
      events: {
        onReady: () => {
          setIsReady(true);
          const p = playerRef.current;
          if (p) {
            try {
              p.setVolume(volume);
            } catch {}
            try {
              // populate available rates from player if possible
              const rates = p.getAvailablePlaybackRates?.();
              if (rates && Array.isArray(rates) && rates.length > 0) {
                setAvailableRates(rates as number[]);
              }
              p.setPlaybackRate?.(playbackRate);
            } catch {}
            // If a video was requested before ready, load and play it now
            if (pendingVideoId) {
              p.loadVideoById({ videoId: pendingVideoId, startSeconds: 0 });
              // Explicitly call playVideo to satisfy autoplay policies under some browsers
              p.playVideo();
              setIsPlaying(true);
              setPendingVideoId(null);
            }
          }
        },
        onStateChange: (e) => {
          if (e.data === window.YT.PlayerState.ENDED) {
            setIsPlaying(false);
            onEnded?.();
          } else if (e.data === window.YT.PlayerState.PLAYING) {
            setIsPlaying(true);
          } else if (e.data === window.YT.PlayerState.PAUSED) {
            setIsPlaying(false);
          }
        },
      },
    });
  }, [apiReady, enabled, onEnded, volume, playbackRate, pendingVideoId]);

  const load = useCallback((videoId: string) => {
    const p = playerRef.current;
    if (!p || !isReady) {
      setPendingVideoId(videoId);
      return;
    }
    p.loadVideoById({ videoId, startSeconds: 0 });
    // Explicit play to avoid relying on autoplay
    p.playVideo();
    setIsPlaying(true);
  }, [isReady]);

  const play = useCallback(() => {
    playerRef.current?.playVideo();
  }, []);

  const pause = useCallback(() => {
    playerRef.current?.pauseVideo();
  }, []);

  const seekTo = useCallback((seconds: number) => {
    const target = Math.max(0, seconds);
    try { playerRef.current?.seekTo(target, true); } catch {}
    setCurrentTime(target);
  }, []);

  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(100, Math.round(v)));
    setVolumeState(clamped);
    try { playerRef.current?.setVolume(clamped); } catch {}
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    setPlaybackRateState(rate);
    try { playerRef.current?.setPlaybackRate?.(rate); } catch {}
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const p = playerRef.current;
      if (!p || !isReady) return;
      try { setCurrentTime(p.getCurrentTime?.() || 0); setDuration(p.getDuration?.() || 0); } catch {}
    }, 250);
    return () => window.clearInterval(timer);
  }, [isReady]);

  useEffect(() => {
    return () => {
      try {
        playerRef.current?.destroy();
      } catch {}
    };
  }, []);

  return {
    mountRef,
    isReady,
    isPlaying,
    volume,
    currentTime,
    duration,
    playbackRate,
    availableRates,
    play,
    pause,
    seekTo,
    load,
    setVolume,
    setPlaybackRate,
  } as const;
};
